import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faCheckCircle, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import type { ApiKeyCreateResponse } from '../../../types/apiKeys'

/**
 * The show-once screen for a key an admin has just minted.
 *
 * Shared by direct issuance and by approving a perpetual-key request, because
 * both end the same way: a plaintext value that exists in this render and
 * nowhere else afterwards. Close stays disabled until the admin confirms they
 * have it — the friction is the point, since dismissing this by accident costs
 * a full re-issue and another round-trip with the account owner.
 */
export default function IssuedKeyReveal({
  created,
  onClose,
  recipient,
}: {
  created: ApiKeyCreateResponse
  onClose: () => void
  /** Who to hand it to. Shown so the admin does not have to remember. */
  recipient?: string | null
}) {
  const [copied, setCopied] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(created.api_key)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl">
        <h2 className="flex items-center text-lg font-semibold text-gray-900">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2 h-5 w-5 text-amber-500" />
          Copy this key now
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          It is stored hashed and cannot be shown again. Send it to{' '}
          {recipient ? <span className="font-medium">{recipient}</span> : 'the account owner'}{' '}
          through a channel you'd trust with a password — not email or chat history.
        </p>

        <code className="mt-4 block w-full break-all rounded border border-gray-300 bg-gray-50 px-4 py-3 font-mono text-sm">
          {created.api_key}
        </code>

        <button
          onClick={handleCopy}
          className="mt-3 inline-flex items-center rounded bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <FontAwesomeIcon icon={copied ? faCheckCircle : faCopy} className="mr-2 h-4 w-4" />
          {copied ? 'Copied' : 'Copy key'}
        </button>

        <label className="mt-5 flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
          />
          <span>I've copied this key and can deliver it to the owner.</span>
        </label>

        <div className="mt-5 flex justify-end">
          <button
            onClick={onClose}
            disabled={!confirmed}
            className="rounded bg-gray-900 px-5 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  )
}
